import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Plus, Send, Mail, Users, Eye, Calendar, Sparkles, FileText,
  Video, Paperclip, X, Check, Loader2, ArrowRight, Trash2,
  Link2, PlayCircle, Image, ChevronDown, ChevronRight, MailCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AI Draft Generator Panel
const AIDraftPanel = ({ onApply, token, leads, selectedLeads }) => {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [productFocus, setProductFocus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, aRes] = await Promise.all([
          axios.get(`${API_URL}/api/videos`, headers),
          axios.get(`${API_URL}/api/assets`, headers)
        ]);
        setVideos(vRes.data);
        setAssets(aRes.data);
      } catch { /* silent */ }
    };
    load();
  }, [token]);

  const selectedLead = leads.find(l => selectedLeads.includes(l.id));
  const PRODUCTS = ['EHR', 'Practice Management', 'RCM', 'CCM', 'RPM', 'APCM', 'AWV'];
  const TONES = ['professional', 'friendly', 'urgent', 'casual', 'formal'];

  const generateDraft = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/ai-draft`, {
        prompt,
        tone,
        recipient_name: selectedLead?.contact_name,
        recipient_org: selectedLead?.organization_name,
        product_focus: productFocus.length ? productFocus : undefined,
        include_videos: selectedVideos.map(v => ({ title: v.title, url: v.video_url })),
        include_assets: selectedAssets.map(a => ({ name: a.name, url: a.file_url }))
      }, headers);
      onApply(res.data.subject, res.data.body_html, selectedAssets, selectedVideos);
      toast.success('AI draft generated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate draft');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4 p-4 bg-gradient-to-br from-violet-50 to-blue-50 rounded-xl border border-violet-100" data-testid="ai-draft-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-violet-600" />
        <h3 className="font-semibold text-slate-800">AI Email Builder</h3>
        <Badge className="bg-violet-100 text-violet-700 text-[10px]">GPT-4o</Badge>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-600">What should this email say?</Label>
          <Textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g. Introduce our EHR solution and schedule a demo, mention time savings..."
            className="mt-1 bg-white border-violet-200 focus:border-violet-400 min-h-[80px]"
            data-testid="ai-prompt"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[140px]">
            <Label className="text-xs text-slate-600">Tone</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {TONES.map(t => (
                <button key={t} onClick={() => setTone(t)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${tone === t ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-violet-50 border border-slate-200'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-600">Product Focus (optional)</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {PRODUCTS.map(p => (
              <button key={p} onClick={() => setProductFocus(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${productFocus.includes(p) ? 'bg-[#1B365D] text-white' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`}
                data-testid={`product-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Video & Asset Pickers */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-xs rounded-full" onClick={() => setShowVideoPicker(!showVideoPicker)} data-testid="toggle-video-picker">
            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Videos ({selectedVideos.length})
          </Button>
          <Button variant="outline" size="sm" className="text-xs rounded-full" onClick={() => setShowAssetPicker(!showAssetPicker)} data-testid="toggle-asset-picker">
            <Paperclip className="w-3.5 h-3.5 mr-1" /> Assets ({selectedAssets.length})
          </Button>
        </div>

        <AnimatePresence>
          {showVideoPicker && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="bg-white rounded-lg border border-slate-200 p-2 max-h-36 overflow-y-auto">
                <p className="text-[10px] text-slate-500 font-medium px-1 mb-1">Select videos to include as links</p>
                {videos.map(v => (
                  <label key={v.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-xs">
                    <Checkbox checked={selectedVideos.some(sv => sv.id === v.id)} onCheckedChange={checked => {
                      setSelectedVideos(prev => checked ? [...prev, v] : prev.filter(sv => sv.id !== v.id));
                    }} />
                    <PlayCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    <span className="truncate">{v.title}</span>
                  </label>
                ))}
                {!videos.length && <p className="text-xs text-slate-400 p-2 text-center">No videos available</p>}
              </div>
            </motion.div>
          )}
          {showAssetPicker && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="bg-white rounded-lg border border-slate-200 p-2 max-h-36 overflow-y-auto">
                <p className="text-[10px] text-slate-500 font-medium px-1 mb-1">Select assets to mention/attach</p>
                {assets.map(a => (
                  <label key={a.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-xs">
                    <Checkbox checked={selectedAssets.some(sa => sa.id === a.id)} onCheckedChange={checked => {
                      setSelectedAssets(prev => checked ? [...prev, a] : prev.filter(sa => sa.id !== a.id));
                    }} />
                    <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    <span className="truncate">{a.name}</span>
                  </label>
                ))}
                {!assets.length && <p className="text-xs text-slate-400 p-2 text-center">No assets available</p>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Button onClick={generateDraft} disabled={loading || !prompt.trim()} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="generate-ai-draft">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Email Draft</>}
        </Button>
      </div>
    </div>
  );
};

// Microsoft Connection Banner
const MSConnectionBanner = ({ connected, email, token }) => (
  <Card className={`border-0 shadow-sm ${connected ? 'bg-green-50/50 ring-1 ring-green-200' : 'bg-amber-50/50 ring-1 ring-amber-200'}`} data-testid="ms-status-banner">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connected ? 'bg-green-100' : 'bg-amber-100'}`}>
        {connected ? <MailCheck className="w-5 h-5 text-green-600" /> : <Mail className="w-5 h-5 text-amber-600" />}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${connected ? 'text-green-800' : 'text-amber-800'}`}>
          {connected ? `Connected: ${email}` : 'Microsoft 365 Not Connected'}
        </p>
        <p className={`text-xs ${connected ? 'text-green-600' : 'text-amber-600'}`}>
          {connected ? 'Emails will be sent from your Microsoft mailbox' : 'Sign in with Microsoft to send real emails from your mailbox'}
        </p>
      </div>
      {!connected && (
        <Button size="sm" className="rounded-full bg-[#0071E3]" data-testid="connect-ms-btn" onClick={async () => {
          try {
            const res = await fetch(`${API_URL}/api/auth/microsoft/auth-url`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.auth_url) window.location.href = data.auth_url;
          } catch { alert('Failed to connect to Microsoft'); }
        }}>Connect Microsoft 365</Button>
      )}
    </CardContent>
  </Card>
);

const Campaigns = () => {
  const { token, user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msConnected, setMsConnected] = useState(false);
  const [msEmail, setMsEmail] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachedAssets, setAttachedAssets] = useState([]);
  const [attachedVideos, setAttachedVideos] = useState([]);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [expandedCampaign, setExpandedCampaign] = useState(null);

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = useCallback(async () => {
    try {
      const [cRes, lRes, msRes] = await Promise.all([
        axios.get(`${API_URL}/api/campaigns`, headers),
        axios.get(`${API_URL}/api/leads`, headers),
        axios.get(`${API_URL}/api/email/status`, headers),
      ]);
      setCampaigns(cRes.data);
      setLeads(lRes.data.filter(l => l.email));
      setMsConnected(msRes.data.connected);
      setMsEmail(msRes.data.email || '');
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAIDraft = (subj, html, assets, videos) => {
    setSubject(subj);
    setBodyHtml(html);
    setAttachedAssets(assets || []);
    setAttachedVideos(videos || []);
  };

  const handleCreateAndSend = async (sendNow = false) => {
    if (!campaignName.trim()) { toast.error('Enter a campaign name'); return; }
    if (!subject.trim()) { toast.error('Enter a subject'); return; }
    if (!bodyHtml.trim()) { toast.error('Enter email body'); return; }
    if (selectedLeads.length === 0) { toast.error('Select at least one recipient'); return; }

    setSending(true);
    try {
      const createRes = await axios.post(`${API_URL}/api/campaigns/create`, {
        name: campaignName,
        subject,
        body_html: bodyHtml,
        lead_ids: selectedLeads,
        attachments: attachedAssets.map(a => ({ name: a.name, url: a.file_url })),
        video_links: attachedVideos.map(v => ({ title: v.title, url: v.video_url })),
      }, headers);

      const campId = createRes.data.campaign_id;

      if (sendNow) {
        if (!msConnected) {
          toast.error('Connect Microsoft 365 first to send emails');
          setSending(false);
          fetchAll();
          return;
        }
        const sendRes = await axios.post(`${API_URL}/api/campaigns/${campId}/send`, {}, headers);
        toast.success(`Campaign sent! ${sendRes.data.sent_count} delivered, ${sendRes.data.failed_count} failed`);
      } else {
        toast.success('Campaign saved as draft');
      }

      // Reset form
      setCampaignName(''); setSubject(''); setBodyHtml('');
      setSelectedLeads([]); setAttachedAssets([]); setAttachedVideos([]);
      setShowBuilder(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  const handleSendExisting = async (campId) => {
    if (!msConnected) { toast.error('Connect Microsoft 365 first'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/campaigns/${campId}/send`, {}, headers);
      toast.success(`Sent! ${res.data.sent_count} delivered, ${res.data.failed_count} failed`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const handleDelete = async (campId) => {
    try {
      await axios.delete(`${API_URL}/api/campaigns/${campId}`, headers);
      toast.success('Campaign deleted');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const toggleLead = (id) => setSelectedLeads(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedLeads(selectedLeads.length === leads.length ? [] : leads.map(l => l.id));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-500">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Email Campaigns</h1>
          <p className="text-slate-500 mt-1">Build AI-powered campaigns and send via Microsoft 365</p>
        </div>
        <Button onClick={() => setShowBuilder(true)} className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="new-campaign-btn">
          <Plus className="w-4 h-4 mr-2" /> New Campaign
        </Button>
      </div>

      {/* Microsoft Status */}
      <MSConnectionBanner connected={msConnected} email={msEmail} token={token} />

      {/* Campaign Builder Dialog */}
      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-heading text-xl">Campaign Builder</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Left: AI + Controls */}
            <div className="lg:col-span-2 p-6 space-y-4 border-r border-slate-100 max-h-[75vh] overflow-y-auto">
              <div>
                <Label className="text-xs">Campaign Name</Label>
                <Input value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="Q1 EHR Outreach" className="mt-1" data-testid="campaign-name-input" />
              </div>

              {/* AI Draft Generator */}
              <AIDraftPanel onApply={handleAIDraft} token={token} leads={leads} selectedLeads={selectedLeads} />

              {/* Recipient Picker */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Recipients ({selectedLeads.length})</Label>
                  <button onClick={selectAll} className="text-[11px] text-[#0071E3] hover:underline">{selectedLeads.length === leads.length ? 'Deselect All' : 'Select All'}</button>
                </div>
                <ScrollArea className="h-40 mt-1 border rounded-lg bg-white">
                  <div className="p-2 space-y-0.5">
                    {leads.map(lead => (
                      <label key={lead.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`lead-${lead.id}`}>
                        <Checkbox checked={selectedLeads.includes(lead.id)} onCheckedChange={() => toggleLead(lead.id)} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{lead.contact_name} - {lead.organization_name}</p>
                          <p className="text-[10px] text-slate-500 truncate">{lead.email}</p>
                        </div>
                      </label>
                    ))}
                    {!leads.length && <p className="text-xs text-slate-400 p-3 text-center">No leads with email addresses</p>}
                  </div>
                </ScrollArea>
              </div>
            </div>

            {/* Right: Email Preview/Editor */}
            <div className="lg:col-span-3 p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <Label className="text-xs">Subject Line</Label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject..." className="mt-1 font-medium" data-testid="subject-input" />
              </div>

              <div>
                <Label className="text-xs">Email Body (HTML)</Label>
                <div className="mt-1 border rounded-lg overflow-hidden">
                  <Textarea
                    value={bodyHtml}
                    onChange={e => setBodyHtml(e.target.value)}
                    placeholder="Compose your email... Use {{contact_name}} and {{organization_name}} for personalization."
                    className="min-h-[200px] border-0 rounded-none font-mono text-xs"
                    data-testid="body-editor"
                  />
                </div>
              </div>

              {/* Preview */}
              {bodyHtml && (
                <div>
                  <Label className="text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</Label>
                  <div className="mt-1 border rounded-lg p-4 bg-white max-h-48 overflow-y-auto prose prose-sm" data-testid="email-preview" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                </div>
              )}

              {/* Attached items */}
              {(attachedAssets.length > 0 || attachedVideos.length > 0) && (
                <div className="space-y-2">
                  <Label className="text-xs">Included Content</Label>
                  <div className="flex flex-wrap gap-2">
                    {attachedAssets.map(a => (
                      <Badge key={a.id} variant="outline" className="text-[10px] gap-1">
                        <FileText className="w-3 h-3" />{a.name}
                        <button onClick={() => setAttachedAssets(prev => prev.filter(x => x.id !== a.id))} className="ml-0.5"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                    {attachedVideos.map(v => (
                      <Badge key={v.id} variant="outline" className="text-[10px] gap-1 border-red-200 text-red-700">
                        <PlayCircle className="w-3 h-3" />{v.title}
                        <button onClick={() => setAttachedVideos(prev => prev.filter(x => x.id !== v.id))} className="ml-0.5"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => handleCreateAndSend(false)} disabled={sending} className="rounded-full flex-1" data-testid="save-draft-btn">
                  Save as Draft
                </Button>
                <Button onClick={() => handleCreateAndSend(true)} disabled={sending || !msConnected} className="rounded-full flex-1 bg-[#0071E3] hover:bg-[#0071E3]/90" data-testid="send-now-btn">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {sending ? 'Sending...' : 'Send Now'}
                </Button>
              </div>
              {!msConnected && <p className="text-[11px] text-amber-600 text-center">Connect Microsoft 365 to enable sending</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign List */}
      {campaigns.length > 0 ? (
        <div className="space-y-3">
          <h2 className="font-semibold text-slate-800">Your Campaigns</h2>
          {campaigns.map(camp => (
            <motion.div key={camp.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="border-0 shadow-sm hover:shadow-md transition-all" data-testid={`campaign-${camp.id}`}>
                <CardContent className="p-0">
                  <div className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${camp.status === 'sent' ? 'bg-green-100' : 'bg-[#1B365D]/10'}`}>
                      {camp.status === 'sent' ? <Check className="w-5 h-5 text-green-600" /> : <Mail className="w-5 h-5 text-[#1B365D]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{camp.name}</p>
                        <Badge variant={camp.status === 'sent' ? 'default' : 'secondary'} className={`text-[10px] capitalize ${camp.status === 'sent' ? 'bg-green-600' : ''}`}>
                          {camp.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{camp.subject}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-[10px] text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> {camp.total_recipients || camp.lead_ids?.length || 0} recipients</span>
                        {camp.status === 'sent' && <span className="text-[10px] text-green-600 flex items-center gap-1"><Send className="w-3 h-3" /> {camp.sent_count} sent</span>}
                        {camp.failed_count > 0 && <span className="text-[10px] text-red-500">{camp.failed_count} failed</span>}
                        <span className="text-[10px] text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(camp.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {camp.status === 'draft' && (
                        <Button size="sm" onClick={() => handleSendExisting(camp.id)} disabled={!msConnected} className="rounded-full bg-[#0071E3] hover:bg-[#0071E3]/90 text-xs" data-testid={`send-${camp.id}`}>
                          <Send className="w-3 h-3 mr-1" /> Send
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(camp.id)} data-testid={`delete-${camp.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-apple">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-violet-500" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Build your first AI-powered campaign</h3>
            <p className="text-slate-500 mb-4 max-w-md mx-auto">Use AI to draft compelling emails, attach collateral, include video links, and send directly from your Microsoft 365 mailbox.</p>
            <Button onClick={() => setShowBuilder(true)} className="bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full" data-testid="create-first-campaign">
              <Plus className="w-4 h-4 mr-2" /> Create Campaign
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Campaigns;
